import { useEffect, useState, useRef } from "react";
import { apiClient } from "@/lib/auth";
import { Bell, PenSquare, Calendar, Lightbulb, X } from "lucide-react";

const TYPE_ICONS = { content: PenSquare, calendar: Calendar, tip: Lightbulb };
const TYPE_COLORS = {
  content: "bg-primary/10 text-primary",
  calendar: "bg-blue-500/10 text-blue-500",
  tip: "bg-amber-500/10 text-amber-500",
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return `il y a ${Math.floor(diff / 86400)} j`;
}

export default function NotificationsCenter() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const ref = useRef(null);

  useEffect(() => {
    const fetch = () => apiClient.get("/notifications").then(r => setData(r.data)).catch(() => {});
    fetch();
    const id = setInterval(fetch, 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)}
        className="relative h-9 w-9 rounded-md hover:bg-accent flex items-center justify-center transition-colors"
        data-testid="notifications-btn"
        aria-label="Notifications">
        <Bell className="h-4 w-4" />
        {data.unread > 0 && (
          <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-background" />
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-[360px] max-w-[calc(100vw-2rem)] rounded-xl border border-border bg-card shadow-xl glass-strong z-50 overflow-hidden fade-in" data-testid="notifications-panel">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div>
              <p className="font-bold text-sm">Notifications</p>
              <p className="text-xs text-muted-foreground">{data.unread} non-lue{data.unread > 1 ? "s" : ""}</p>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-accent rounded-md" aria-label="Fermer">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {data.items.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                <Bell className="h-8 w-8 mx-auto mb-2 opacity-40" />
                Aucune notification.
              </div>
            ) : data.items.map(n => {
              const Icon = TYPE_ICONS[n.type] || Bell;
              return (
                <div key={n.id} className="px-4 py-3 border-b border-border last:border-0 hover:bg-accent/40 transition-colors flex items-start gap-3" data-testid={`notif-${n.id}`}>
                  <div className={`h-8 w-8 rounded-md flex items-center justify-center shrink-0 ${TYPE_COLORS[n.type] || "bg-muted"}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{n.title}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2">{n.body}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">{timeAgo(n.at)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
